<template>
  <v-container fluid class="pa-0 rr-access-page">
    <div
      class="rr-access-toolbar bg-red-darken-3 py-2 py-md-3 px-3 px-md-6"
    >
      <img
        :src="rrLogo"
        height="36"
        width="36"
        class="flex-shrink-0"
        :alt="$t('rete_ricambi_user_access.logo_alt')"
      />
      <div
        class="text-subtitle-1 text-white text-truncate flex-grow-1 min-width-0"
      >
        {{ $t("rete_ricambi_user_access.title") }}
      </div>
    </div>

    <div class="internal-admin pa-3 pa-sm-4 pa-md-6">
      <template v-if="!sessionAllowed">
        <v-alert type="error" border="start" prominent>
          {{ $t("rete_ricambi_user_access.unauthorized") }}
        </v-alert>
      </template>

      <template v-else>
        <p class="internal-admin__hint mb-4">
          {{ $t("rete_ricambi_user_access.hint_before") }}<strong>{{ $t("rete_ricambi_user_access.hint_emphasis") }}</strong>{{ $t("rete_ricambi_user_access.hint_after") }}
        </p>

        <div class="internal-admin__search mb-4">
          <v-text-field
            v-model="searchEmail"
            :label="$t('rete_ricambi_user_access.search_label')"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            :placeholder="placeholderEmailExample"
            @keyup.enter="searchUser"
          />
          <v-btn
            color="#208f5c"
            class="text-white internal-admin__search-btn flex-shrink-0"
            variant="flat"
            :loading="loadingSearch"
            @click="searchUser"
          >
            {{ $t("rete_ricambi_user_access.search_button") }}
          </v-btn>
        </div>

        <small
          v-if="searchMessage"
          class="d-block mb-2"
          :class="searchMessageIsInfo ? 'text-info' : 'text-medium-emphasis'"
        >{{ searchMessage }}</small>
        <small v-if="actionError" class="d-block mb-2 text-error">{{ actionError }}</small>

        <v-card v-if="matchedUsers.length" class="internal-admin__results" variant="outlined">
          <template v-for="(u, idx) in matchedUsers" :key="u._id">
            <v-divider v-if="idx > 0" />
            <div class="internal-admin__user-row">
              <div class="internal-admin__user-info">
                <div class="internal-admin__user-email">{{ u.email || u.username || '—' }}</div>
                <div class="internal-admin__user-name text-medium-emphasis">
                  {{ [u.name, u.surname].filter(Boolean).join(' ') || $t('rete_ricambi_user_access.no_name') }}
                </div>
                <div class="internal-admin__chips mt-4">
                  <v-chip
                    class="internal-admin__status-chip text-white"
                    :color="u.account?.approved === true ? 'success' : 'warning'"
                    variant="flat"
                    size="large"
                    :prepend-icon="u.account?.approved === true ? 'mdi-check-decagram' : 'mdi-account-clock'"
                  >
                    {{ u.account?.approved === true ? $t('rete_ricambi_user_access.status_approved') : $t('rete_ricambi_user_access.status_not_approved') }}
                  </v-chip>
                  <v-chip
                    v-if="u.account?.disabled"
                    color="error"
                    variant="flat"
                    size="large"
                    class="internal-admin__status-chip text-white"
                    prepend-icon="mdi-lock-alert"
                  >
                    {{ $t("rete_ricambi_user_access.status_disabled") }}
                  </v-chip>
                </div>
              </div>
              <div class="internal-admin__actions">
                <v-btn
                  size="small"
                  color="success"
                  variant="flat"
                  class="text-white"
                  :loading="pendingId === u._id && pendingAction === 'approve'"
                  :disabled="u.account?.approved === true"
                  @click="setUserApproved(u, true)"
                >
                  {{ $t("rete_ricambi_user_access.approve_button") }}
                </v-btn>
                <v-btn
                  size="small"
                  color="error"
                  variant="flat"
                  class="text-white"
                  :loading="pendingId === u._id && pendingAction === 'disable'"
                  :disabled="u.account?.approved === false"
                  @click="setUserApproved(u, false)"
                >
                  {{ $t("rete_ricambi_user_access.revoke_button") }}
                </v-btn>
              </div>
            </div>
          </template>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import rrLogo from "@/assets/rr-logo.png";
import config from "@/config";
import { isReteRicambiInternalAdminEmail } from "@/constants/reteRicambiInternalAdminAllowlist";

function extractApiErrorMessage(data) {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (typeof data.message === "string" && data.message) return data.message;
  if (data.data && typeof data.data === "object" && typeof data.data.message === "string") {
    return data.data.message;
  }
  return "";
}

function authHeaders() {
  const token = localStorage.getItem("token");
  const headers = {
    Accept: "application/json",
  };
  if (token) headers.Authorization = token;
  return headers;
}

function authHeadersJson() {
  return {
    ...authHeaders(),
    "Content-Type": "application/json",
  };
}

export default {
  name: "ReteRicambiUserAccess",

  data() {
    return {
      rrLogo,
      /** Plain string: @ cannot live in vue-i18n messages (reserved for linked syntax). */
      placeholderEmailExample: "target@example.com",
      searchEmail: "",
      loadingSearch: false,
      searchMessage: "",
      searchMessageIsInfo: false,
      actionError: "",
      matchedUsers: [],
      pendingId: null,
      pendingAction: null,
    };
  },

  computed: {
    userEmail() {
      return this.$store.getters.userEmail || "";
    },
    sessionAllowed() {
      return isReteRicambiInternalAdminEmail(this.userEmail);
    },
    apiBaseUrl() {
      return config.getServerUri();
    },
  },

  methods: {
    axiosErrorMessage(error, fallbackKey) {
      const status = error?.response?.status;
      const raw = error?.response?.data;
      const text = extractApiErrorMessage(raw);
      const t = (key) => this.$t(`rete_ricambi_user_access.${key}`);
      if (status === 401) return t("error_auth");
      if (status === 403) return t("error_forbidden");
      if (status === 404) return t("error_not_found");
      if (status === 503) return text || t("error_db_not_configured");
      if (status === 400 && text) return text;
      if (text) return text;
      return t(fallbackKey);
    },

    searchUser() {
      this.searchMessage = "";
      this.searchMessageIsInfo = false;
      this.actionError = "";
      const q = (this.searchEmail || "").trim();
      if (!q) {
        this.searchMessage = this.$t("rete_ricambi_user_access.error_enter_email");
        this.matchedUsers = [];
        return;
      }
      const adminEmail = (this.userEmail || "").trim();
      if (q.toLowerCase() === adminEmail.toLowerCase()) {
        this.matchedUsers = [];
        this.searchMessageIsInfo = true;
        this.searchMessage = this.$t("rete_ricambi_user_access.error_self_search");
        return;
      }
      this.loadingSearch = true;
      axios
        .get(`${this.apiBaseUrl}/rete-ricambi/by-email`, {
          params: { email: q },
          headers: authHeaders(),
        })
        .then((res) => {
          const user = res.data?.user;
          this.matchedUsers = user ? [user] : [];
          if (!user) {
            this.searchMessage = this.$t("rete_ricambi_user_access.error_no_user");
          }
        })
        .catch((error) => {
          this.matchedUsers = [];
          this.searchMessage = this.axiosErrorMessage(error, "error_search_failed");
        })
        .finally(() => {
          this.loadingSearch = false;
        });
    },

    setUserApproved(user, approved) {
      const email = (user?.email || "").trim();
      if (!email) return;
      this.actionError = "";
      this.pendingId = user._id;
      this.pendingAction = approved ? "approve" : "disable";
      axios
        .put(
          `${this.apiBaseUrl}/rete-ricambi/account-approved`,
          { email, approved },
          { headers: authHeadersJson() },
        )
        .then(() => {
          const idx = this.matchedUsers.findIndex((x) => x._id === user._id);
          if (idx !== -1) {
            const merged = {
              ...this.matchedUsers[idx],
              account: {
                ...this.matchedUsers[idx].account,
                approved,
              },
            };
            this.matchedUsers.splice(idx, 1, merged);
          }
        })
        .catch((error) => {
          this.actionError = this.axiosErrorMessage(error, "error_update_failed");
        })
        .finally(() => {
          this.pendingId = null;
          this.pendingAction = null;
        });
    },
  },
};
</script>

<style scoped>
.rr-access-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 12px;
  row-gap: 8px;
}

.rr-access-page .min-width-0 {
  min-width: 0;
}
.internal-admin {
  max-width: 960px;
  margin: 0 auto;
  min-height: min(320px, 50vh);
}

.internal-admin__hint {
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
}

.internal-admin__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.internal-admin__signed-in {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.7);
}

.internal-admin__search {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.internal-admin__search :deep(.v-text-field) {
  flex: 1 1 220px;
  min-width: 0;
}

.internal-admin__search-btn {
  width: 100%;
}

@media (min-width: 600px) {
  .internal-admin__search-btn {
    width: auto;
    align-self: flex-start;
  }
}

.internal-admin__user-row {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
}

@media (min-width: 720px) {
  .internal-admin__user-row {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }
}

.internal-admin__user-info {
  flex: 1;
  min-width: 0;
}

.internal-admin__user-email {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.87);
  word-break: break-all;
}

.internal-admin__user-name {
  font-size: 14px;
  margin-top: 6px;
  line-height: 1.4;
}

.internal-admin__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.internal-admin__status-chip {
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: none;
  min-height: 40px;
  padding-inline: 14px;
}

.internal-admin__status-chip :deep(.v-chip__content) {
  font-size: 1rem;
}

.internal-admin__user-row .internal-admin__actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

@media (min-width: 720px) {
  .internal-admin__user-row .internal-admin__actions {
    width: auto;
    flex-direction: row;
    align-items: flex-start;
  }
}
</style>
